import React, { Suspense, useEffect, useMemo, useRef } from 'react'

import type { CarouselProps } from '@awazevr/design-system.components.carousel'
import type { IPropertyCard } from '@awazevr/design-system.compositions.property-card'
import { breakpoints, useWindowWidth } from '@awazevr/design-system.utils.react'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'

import {
  PropertiesNearbyCarouselWrapper,
  PropertiesNearbyContainer,
  PropertiesNearbyHeader,
  PropertiesNearbyHeading,
  ViewMoreButton,
} from './PropertiesNearby.style'

// Lazy-loaded: the shared carousel ships ESM and breaks the sync import graph.
const PropertyCardCarousel = React.lazy(() =>
  import('@awazevr/design-system.compositions.property-card-carousel').then(
    (module) => ({ default: module.PropertyCardCarousel }),
  ),
)

// Exported so the data-layer events describe the same capped set.
export const MAX_CARDS = 8
const CONTROLLER_ID = 'properties-nearby'
// With only 1-2 cards the carousel would stretch them across the full track
// (a lone card skips Swiper entirely), so cap the rendered width to the cards'
// natural size and centre them.
const CARD_MAX_WIDTH = 341
const CARD_GAP = 24
// First/last card gutter (mobile) while the track bleeds to the edges.
const MOBILE_CAROUSEL_OFFSET = 16

export interface PropertiesNearbyProps {
  properties: IPropertyCard[]
  onViewMore: () => void
  onCardClick: (code: string, position: number) => void
  onSeen: () => void
}

export const PropertiesNearby = ({
  properties,
  onViewMore,
  onCardClick,
  onSeen,
}: PropertiesNearbyProps): JSX.Element | null => {
  const { formatMessage } = useIntlContext()
  const width = useWindowWidth() ?? 0
  const isMobile = width > 0 && width < breakpoints[0]

  // Key the "seen" guard on the visible set, not a one-shot flag: this component
  // doesn't unmount on SPA filter changes, so a new set of expanded properties
  // must re-fire the impression (mirrors the low-results re-fire in
  // SearchResults). Same set re-rendering does not.
  const seenKey = useMemo(
    () =>
      properties
        .slice(0, MAX_CARDS)
        .map((property) => property.code)
        .join('|'),
    [properties],
  )
  const lastSeenKeyRef = useRef<string | null>(null)
  const carouselRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (properties.length === 0 || seenKey === lastSeenKeyRef.current) {
      return
    }
    lastSeenKeyRef.current = seenKey
    onSeen()
  }, [onSeen, seenKey, properties.length])

  // Two Swiper tweaks the DS Carousel doesn't expose as props, re-applied via an
  // observer (lazy mount + Swiper re-adds a11y): (1) strip the nav wrapper's
  // duplicate role/tabindex so the inner <button> is the single control;
  // (2) set slidesOffsetBefore/After (mobile) to keep the first/last card gutter.
  useEffect(() => {
    const root = carouselRef.current
    if (!root) {
      return
    }
    const offset = isMobile ? String(MOBILE_CAROUSEL_OFFSET) : '0'
    const applyCarouselFixes = (): void => {
      root
        .querySelectorAll<HTMLElement>('.swiper-prev, .swiper-next')
        .forEach((nav) => {
          if (nav.getAttribute('tabindex') !== '-1') {
            nav.setAttribute('tabindex', '-1')
          }
          nav.removeAttribute('role')
          nav.removeAttribute('aria-label')
        })
      // Only write when the value actually changes: each setAttribute on the
      // Swiper element triggers a swiper.update(), and the observer fires on
      // every nav a11y change (e.g. at the ends), so re-writing would re-clamp
      // the scroll mid-gesture and lock the carousel at the end.
      const swiper = root.querySelector('swiper-container')
      if (swiper && swiper.getAttribute('slides-offset-before') !== offset) {
        swiper.setAttribute('slides-offset-before', offset)
        swiper.setAttribute('slides-offset-after', offset)
      }
    }
    applyCarouselFixes()
    const observer = new MutationObserver(applyCarouselFixes)
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex', 'role', 'aria-label'],
    })
    return () => observer.disconnect()
  }, [isMobile])

  const cards = useMemo(
    () =>
      properties.slice(0, MAX_CARDS).map((property, index) => ({
        ...property,
        onPropertyLinkClick: () => {
          onCardClick(String(property.code), index + 1)
          property.onPropertyLinkClick?.()
        },
      })),
    [properties, onCardClick],
  )

  if (properties.length === 0) {
    return null
  }

  const cardCount = cards.length
  // Centre 1-2 cards only on tablet/desktop; mobile keeps the peek carousel.
  const fewCards = !isMobile && cardCount <= 2
  // We trim to MAX_CARDS for display. When the source has more, the link is
  // "View more"; when we're already showing them all it's "View all" (the
  // expanded page still adds value — filtering, sorting, a full grid).
  const hasMoreThanShown = properties.length > MAX_CARDS
  // Centre 1-2 cards at their natural size instead of stretching them.
  const fewCardsMaxWidth =
    cardCount * CARD_MAX_WIDTH + (cardCount - 1) * CARD_GAP

  const carouselProps = {
    children: <span />,
    loop: false,
    hideNavButtons: isMobile,
    swiperOptions: {
      // Fractional slidesPerView gives the peek. Sits inside a definite
      // grid-track width, so Swiper's set-wrapper-size stays bounded.
      // Mobile advances 1 card at a time; tablet/desktop advance 2.
      slidesPerView: 1.2,
      slidesPerGroup: 1,
      spaceBetween: CARD_GAP,
      ...(width >= breakpoints[0] && {
        // Cap to the card count so 2 cards fill the track without an empty peek.
        slidesPerView: Math.min(cardCount, 2.5),
        slidesPerGroup: 2,
      }),
      keyboard: false,
      controller: {
        control: CONTROLLER_ID,
      },
    },
    customPagination: {
      type: 'bullets',
      dynamicBullets: false,
      dynamicMainBullets: 1,
    },
    translations: {
      previousNavigation: formatMessage({
        id: 'property-carousel.previousNavigation',
        defaultMessage: 'Previous slide',
      }),
      nextNavigation: formatMessage({
        id: 'property-carousel.nextNavigation',
        defaultMessage: 'Next slide',
      }),
    },
  } satisfies CarouselProps

  const heading = formatMessage({
    id: 'search-results.properties-nearby.heading',
    defaultMessage: 'Properties nearby',
  })

  return (
    <PropertiesNearbyContainer
      aria-label={heading}
      data-testid="properties-nearby"
    >
      <PropertiesNearbyHeader>
        <PropertiesNearbyHeading>{heading}</PropertiesNearbyHeading>
        <ViewMoreButton
          type="button"
          onClick={onViewMore}
          data-testid="properties-nearby-view-more"
        >
          {formatMessage(
            hasMoreThanShown
              ? {
                  id: 'search-results.properties-nearby.view-more',
                  defaultMessage: 'View more',
                }
              : {
                  id: 'search-results.properties-nearby.view-all',
                  defaultMessage: 'View all',
                },
          )}
        </ViewMoreButton>
      </PropertiesNearbyHeader>

      <PropertiesNearbyCarouselWrapper
        ref={carouselRef}
        $hidePagination={fewCards}
        style={
          fewCards
            ? {
                maxWidth: fewCardsMaxWidth,
                marginLeft: 'auto',
                marginRight: 'auto',
              }
            : undefined
        }
      >
        <Suspense fallback={null}>
          <PropertyCardCarousel
            carouselProps={carouselProps}
            propertyCardsData={cards}
          />
        </Suspense>
      </PropertiesNearbyCarouselWrapper>
    </PropertiesNearbyContainer>
  )
}
